import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Forgot password state
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMsg, setForgotMsg] = useState("");
  const [forgotError, setForgotError] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem("admin_token", data.token);
      localStorage.setItem("admin_name", data.name);
      navigate("/admin/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setForgotError("");
    setForgotMsg("");
    setForgotLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/forgot-password`, { email: forgotEmail });
      setForgotMsg(data.message);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setForgotError(typeof detail === "string" ? detail : "Request failed");
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white">
            Paperwork<span className="text-amber-400">Depot</span>
          </h1>
          <p className="text-zinc-500 mt-2 text-sm tracking-wide uppercase">Admin Dashboard</p>
        </div>

        {!showForgot ? (
          <form
            onSubmit={handleLogin}
            className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 space-y-5"
            data-testid="admin-login-form"
          >
            {error && (
              <div className="bg-red-950 border border-red-800 text-red-300 text-sm px-4 py-3 rounded-lg" data-testid="login-error">
                {error}
              </div>
            )}

            <div>
              <label className="block text-zinc-400 text-sm mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="admin@paperworkdepot.com"
                required
                data-testid="login-email-input"
              />
            </div>

            <div>
              <label className="block text-zinc-400 text-sm mb-1.5">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="Enter password"
                required
                data-testid="login-password-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-amber-500 hover:bg-amber-400 text-black font-bold py-3 rounded-lg transition-colors disabled:opacity-50"
              data-testid="login-submit-button"
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => setShowForgot(true)}
                className="text-zinc-500 hover:text-amber-400 text-sm transition-colors"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </button>
            </div>
          </form>
        ) : (
          <form
            onSubmit={handleForgot}
            className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 space-y-5"
            data-testid="forgot-password-form"
          >
            <h2 className="text-white text-lg font-bold">Reset Your Password</h2>
            <p className="text-zinc-500 text-sm">Enter your admin email. We'll send a reset link that expires in 30 minutes.</p>

            {forgotMsg && (
              <div className="bg-green-950 border border-green-800 text-green-300 text-sm px-4 py-3 rounded-lg" data-testid="forgot-success">
                {forgotMsg}
              </div>
            )}
            {forgotError && (
              <div className="bg-red-950 border border-red-800 text-red-300 text-sm px-4 py-3 rounded-lg" data-testid="forgot-error">
                {forgotError}
              </div>
            )}

            <div>
              <label className="block text-zinc-400 text-sm mb-1.5">Email</label>
              <input
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="admin@paperworkdepot.com"
                required
                data-testid="forgot-email-input"
              />
            </div>

            <button
              type="submit"
              disabled={forgotLoading}
              className="w-full bg-amber-500 hover:bg-amber-400 text-black font-bold py-3 rounded-lg transition-colors disabled:opacity-50"
              data-testid="forgot-submit-button"
            >
              {forgotLoading ? "Sending..." : "Send Reset Link"}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => { setShowForgot(false); setForgotMsg(""); setForgotError(""); }}
                className="text-zinc-500 hover:text-amber-400 text-sm transition-colors"
                data-testid="back-to-login-link"
              >
                Back to login
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
